import { CloseIcon } from '@chakra-ui/icons'
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  Tag,
  TagLabel,
  Text,
  VStack,
} from '@chakra-ui/react'
import type { ICD10, ICD10Group } from '@hb/shared'
import { EncounterType, suggestUltrasoundIcdCodes } from '@hb/shared'
import { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react'
import { useField, useForm } from 'react-final-form'
import { useICD10Groups } from '../../../../hooks/backend/icd10/useICD10Groups'
import { SectionLabel } from '../../SectionLabel'
import { SuggestedICDCodes } from './SuggestedICDCodes'

const buildZ3ACode = (days: number) => {
  const week = Math.floor(days / 7)
  if (week < 6 || week > 42) return null
  return `Z3A.${String(week).padStart(2, '0')}`
}

// ─── Fuzzy search ──────────────────────────────────────────────────────────────

// Levenshtein edit distance, O(n) space
function levenshtein(a: string, b: string): number {
  if (a.length === 0) return b.length
  if (b.length === 0) return a.length
  if (Math.abs(a.length - b.length) > Math.floor(Math.max(a.length, b.length) * 0.6)) {
    return Math.max(a.length, b.length)
  }
  const prev = Array.from({ length: b.length + 1 }, (_, i) => i)
  for (let i = 1; i <= a.length; i++) {
    let prevDiag = prev[0]
    prev[0] = i
    for (let j = 1; j <= b.length; j++) {
      const temp = prev[j]
      prev[j] = a[i - 1] === b[j - 1] ? prevDiag : 1 + Math.min(prevDiag, prev[j], prev[j - 1])
      prevDiag = temp
    }
  }
  return prev[b.length]
}

function wordMatchScore(queryWord: string, targetWord: string): number {
  if (targetWord === queryWord) return 10
  if (targetWord.startsWith(queryWord)) return 7
  if (targetWord.includes(queryWord)) return 5
  if (queryWord.length >= 4) {
    const maxDist = Math.max(1, Math.floor(queryWord.length * 0.35))
    if (levenshtein(queryWord, targetWord) <= maxDist) return 2
  }
  return 0
}

function scoreIcd(query: string, icd: ICD10): number {
  const q = query.toLowerCase().trim()
  if (q.length < 2) return 0

  const desc = icd.description.toLowerCase()
  const kwds = (icd.keywords ?? []).map(k => k.toLowerCase())
  const allPhrases = [desc, ...kwds]
  const queryWords = q.split(/\s+/).filter(w => w.length >= 2)

  if (queryWords.length === 0) return 0

  let total = 0

  for (const qw of queryWords) {
    let best = 0

    for (const phrase of allPhrases) {
      if (phrase.includes(qw)) {
        best = Math.max(best, phrase.startsWith(qw) ? 8 : 5)
      }
    }

    if (best === 0) {
      const allTokens = allPhrases.flatMap(p => p.split(/\W+/).filter(Boolean))
      for (const token of allTokens) {
        best = Math.max(best, wordMatchScore(qw, token))
        if (best >= 7) break
      }
    }

    if (best === 0 && queryWords.length > 1) return 0

    total += best
  }

  return total
}

const SEARCH_PAGE_SIZE = 10

function searchIcdCodes(query: string, groups: ICD10Group[], selectedCodes: string[]): ICD10[] {
  const q = query.trim()
  if (q.length < 2) return []

  const allCodes = groups.flatMap(g => g.codes).filter(c => !c.code.startsWith('Z3A.'))

  return allCodes
    .map(c => ({ c, score: scoreIcd(q, c) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => {
      const aSelected = selectedCodes.includes(a.c.code)
      const bSelected = selectedCodes.includes(b.c.code)
      if (aSelected !== bSelected) return aSelected ? 1 : -1
      return b.score - a.score || a.c.description.localeCompare(b.c.description)
    })
    .map(({ c }) => c)
}

// ─── Component ─────────────────────────────────────────────────────────────────

export const ICD10SelectionInput = ({ encounterType }: { encounterType?: EncounterType }) => {
  const form = useForm()
  const [searchQuery, setSearchQuery] = useState('')
  const [showAllResults, setShowAllResults] = useState(false)

  const allGroups = useICD10Groups()

  // §1.2.1 COMPLIANCE: Pre-Conception Consultation must never surface O-codes (pregnancy codes).
  // Filter out any ICD group whose codes start with 'O' when this is a pre-conception encounter.
  const activeGroups = useMemo(
    () =>
      encounterType === EncounterType.PRE_CONCEPTION
        ? allGroups.filter(g => !g.codes.some(c => c.code.startsWith('O')))
        : allGroups,
    [allGroups, encounterType],
  )

  const icdDescriptionMap = useMemo(
    () => new Map(activeGroups.flatMap(g => g.codes.map(c => [c.code, c.description]))),
    [activeGroups],
  )

  const { input: codesInput } = useField<string[] | undefined>('data.icd10Codes', {
    subscription: { value: true },
  })
  const {
    input: { value: gestationalAgeDays },
  } = useField<number | undefined>('data.gestationalAge', { subscription: { value: true } })

  // Ultrasound-specific fields — always subscribed (hooks can't be conditional);
  // values are only used when encounterType === ULTRASOUND.
  const {
    input: { value: fetalPresentation },
  } = useField<string | undefined>('data.fetalPresentation', { subscription: { value: true } })
  const {
    input: { value: placentalLocation },
  } = useField<string | undefined>('data.placentalLocation', { subscription: { value: true } })
  const {
    input: { value: amnioticFluidType },
  } = useField<string | undefined>('data.amnioticFluidType', { subscription: { value: true } })
  const {
    input: { value: amnioticFluidValue },
  } = useField<number | undefined>('data.amnioticFluidValue', { subscription: { value: true } })
  const {
    input: { value: cervicalLength },
  } = useField<number | undefined>('data.cervicalLength', { subscription: { value: true } })
  const {
    input: { value: fetalCardiacActivity },
  } = useField<string | undefined>('data.fetalCardiacActivity', { subscription: { value: true } })
  const {
    input: { value: gaAtStudyDays },
  } = useField<number | undefined>('data.gaAtStudyDays', { subscription: { value: true } })

  const allCodes: string[] = useMemo(
    () => (Array.isArray(codesInput.value) ? codesInput.value : []),
    [codesInput.value],
  )

  const displayCodes = useMemo(() => allCodes.filter(c => !c.startsWith('Z3A.')), [allCodes])

  const ultrasoundSuggestions = useMemo(() => {
    if (encounterType !== EncounterType.ULTRASOUND) return []
    // useField returns `string` — cast to the narrower union types that UltrasoundData expects
    return suggestUltrasoundIcdCodes({
      fetalPresentation,
      placentalLocation,
      amnioticFluidType,
      amnioticFluidValue,
      cervicalLength,
      fetalCardiacActivity,
      gaAtStudyDays,
    } as Parameters<typeof suggestUltrasoundIcdCodes>[0]).filter(
      code => !displayCodes.includes(code),
    )
  }, [
    encounterType,
    fetalPresentation,
    placentalLocation,
    amnioticFluidType,
    amnioticFluidValue,
    cervicalLength,
    fetalCardiacActivity,
    gaAtStudyDays,
    displayCodes,
  ])

  // Keep the Z3A code in sync whenever GA changes.
  // §1.2.1: Skipped for pre-conception encounters — no gestational age context.
  useEffect(() => {
    if (encounterType === EncounterType.PRE_CONCEPTION) return
    if (gestationalAgeDays == null) return
    const z3aCode = buildZ3ACode(gestationalAgeDays)
    const withoutZ3A = allCodes.filter(c => !c.startsWith('Z3A.'))
    const next = z3aCode ? [...withoutZ3A, z3aCode] : withoutZ3A
    const hasChanged = next.length !== allCodes.length || next.some((c, i) => c !== allCodes[i])
    if (hasChanged) {
      form.change('data.icd10Codes', next)
    }
  }, [gestationalAgeDays]) // eslint-disable-line react-hooks/exhaustive-deps

  const addCode = useCallback(
    (code: string) => {
      if (displayCodes.includes(code)) return
      const withoutZ3A = allCodes.filter(c => !c.startsWith('Z3A.'))
      const z3aCode = gestationalAgeDays != null ? buildZ3ACode(gestationalAgeDays) : null
      form.change('data.icd10Codes', [...withoutZ3A, code, ...(z3aCode ? [z3aCode] : [])])
    },
    [allCodes, displayCodes, gestationalAgeDays, form],
  )

  const handleRemoveCode = useCallback(
    (code: string) => {
      form.change(
        'data.icd10Codes',
        allCodes.filter(c => c !== code),
      )
    },
    [allCodes, form],
  )

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    setShowAllResults(false)
  }, [])

  const deferredQuery = useDeferredValue(searchQuery)

  const searchResults = useMemo(
    () => searchIcdCodes(deferredQuery, activeGroups, displayCodes),
    [deferredQuery, activeGroups, displayCodes],
  )

  const visibleResults = showAllResults ? searchResults : searchResults.slice(0, SEARCH_PAGE_SIZE)
  const hiddenCount = searchResults.length - SEARCH_PAGE_SIZE

  return (
    <Box w="100%" px={4} py={3}>
      <SectionLabel mb={1}>Diagnosis</SectionLabel>

      {/* Ultrasound finding-based suggestions */}
      {ultrasoundSuggestions.length > 0 && (
        <SuggestedICDCodes codes={ultrasoundSuggestions} onCodeClick={addCode} loading={false} />
      )}

      {/* Search — plain full-width input matching the mockup Diagnosis searchbar:
          0.5px #ccc border · 8px radius · 12px #333 text · white bg · sage focus (spec 0.6.5). */}
      <Box w="100%">
        <Input
          variant="unstyled"
          placeholder="Search by description — e.g. 'routine prenatal' or 'high blood pressure'..."
          value={searchQuery}
          onChange={handleSearchChange}
          w="100%"
          h="auto"
          fontFamily="hero-new"
          color="#333333"
          bg="white"
          border="0.5px solid"
          borderColor="#cccccc"
          borderRadius="8px"
          fontSize="xs"
          px="10px"
          py="6px"
          transition="border-color 0.15s ease"
          _focus={{ borderColor: '#4a7c59' }}
          _focusVisible={{ borderColor: '#4a7c59' }}
          _placeholder={{ color: '#aaaaaa' }}
          _dark={{ bg: 'gray.800' }}
        />
      </Box>

      {/* Search results */}
      {deferredQuery.length >= 2 && (
        <Box mt={2} pl={1}>
          {searchResults.length === 0 ? (
            <Text fontSize="xs" color="gray.500" fontStyle="italic">
              No matching diagnoses — try different terms or browse by category.
            </Text>
          ) : (
            <>
              <Flex flexWrap="wrap" gap={2}>
                {visibleResults.map(icd => {
                  const alreadySelected = displayCodes.includes(icd.code)
                  return (
                    <Tag
                      key={icd.code}
                      size="sm"
                      cursor={alreadySelected ? 'default' : 'pointer'}
                      colorScheme={alreadySelected ? 'green' : 'blue'}
                      variant={alreadySelected ? 'solid' : 'subtle'}
                      onClick={() => !alreadySelected && addCode(icd.code)}
                      opacity={alreadySelected ? 0.65 : 1}
                      _hover={alreadySelected ? {} : { opacity: 0.8 }}>
                      <TagLabel>{icd.description}</TagLabel>
                    </Tag>
                  )
                })}
              </Flex>
              {!showAllResults && hiddenCount > 0 && (
                <Button
                  size="xs"
                  variant="link"
                  colorScheme="gray"
                  mt={1.5}
                  onClick={() => setShowAllResults(true)}>
                  Show {hiddenCount} more
                </Button>
              )}
            </>
          )}
        </Box>
      )}

      {/* Category browser */}
      <Accordion allowMultiple reduceMotion mt={3}>
        <AccordionItem border="none">
          {({ isExpanded }) => (
            <>
              <AccordionButton px={0} py={1} _hover={{ bg: 'transparent' }}>
                <Flex
                  fontSize="xs"
                  py={1}
                  pl={3}
                  pr={2}
                  align="center"
                  borderRadius={6}
                  h={7}
                  border="1px solid"
                  borderColor={isExpanded ? 'green.600' : 'gray.300'}
                  color={isExpanded ? 'green.600' : 'gray.600'}
                  fontWeight={600}
                  _dark={{ color: isExpanded ? 'green.300' : 'gray.400' }}>
                  <Text flex={1} minW="0">
                    Browse by category
                  </Text>
                  <AccordionIcon color="gray.400" boxSize={3} ml={1} />
                </Flex>
              </AccordionButton>
              <AccordionPanel px={0} pt={1} pb={2}>
                <Accordion allowMultiple reduceMotion>
                  {activeGroups.map(group => (
                    <AccordionItem
                      key={group.name}
                      border="1px solid"
                      borderColor="gray.200"
                      _dark={{ borderColor: 'gray.700' }}
                      borderRadius="md"
                      mb={1}>
                      <AccordionButton py={1.5} px={3}>
                        <Box flex={1} textAlign="left">
                          <Text
                            fontSize="xs"
                            fontWeight={600}
                            color="gray.600"
                            _dark={{ color: 'gray.300' }}>
                            {group.name}
                          </Text>
                        </Box>
                        <AccordionIcon boxSize={3} />
                      </AccordionButton>
                      <AccordionPanel pb={2} pt={1} px={3}>
                        <Flex flexWrap="wrap" gap={1.5}>
                          {group.codes
                            .filter(c => !c.code.startsWith('Z3A.'))
                            .map(icd => {
                              const alreadySelected = displayCodes.includes(icd.code)
                              return (
                                <Tag
                                  key={icd.code}
                                  size="sm"
                                  cursor={alreadySelected ? 'default' : 'pointer'}
                                  colorScheme={alreadySelected ? 'green' : 'gray'}
                                  variant={alreadySelected ? 'solid' : 'outline'}
                                  onClick={() => !alreadySelected && addCode(icd.code)}
                                  opacity={alreadySelected ? 0.65 : 1}
                                  _hover={
                                    alreadySelected
                                      ? {}
                                      : { borderColor: 'blue.400', color: 'blue.600' }
                                  }>
                                  <TagLabel>{icd.description}</TagLabel>
                                </Tag>
                              )
                            })}
                        </Flex>
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </Accordion>
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      </Accordion>

      {/* Selected codes */}
      {displayCodes.length > 0 && (
        <VStack data-child-name="data.icd10Codes" align="flex-start" spacing={0} mt={1}>
          <Text fontSize="xs" color="gray.600" fontWeight={600} _dark={{ color: 'gray.400' }}>
            Selected diagnoses:
          </Text>
          <Flex flexWrap="wrap" gap={2}>
            {displayCodes.map(code => {
              const description: string = icdDescriptionMap.get(code) ?? code
              return (
                <Tag key={code} size="md" colorScheme="green" variant="subtle">
                  <TagLabel>{description}</TagLabel>
                  <IconButton
                    aria-label={`Remove ${description}`}
                    icon={<CloseIcon boxSize={2} />}
                    size="xs"
                    variant="ghost"
                    ml={1}
                    onClick={() => handleRemoveCode(code)}
                  />
                </Tag>
              )
            })}
          </Flex>
        </VStack>
      )}

      {displayCodes.length === 0 && (
        <HStack data-child-name="data.icd10Codes">
          <Text fontSize="sm" py={1} px={1} color="red.500">
            At least one diagnosis is required.
          </Text>
        </HStack>
      )}
    </Box>
  )
}
